import { Button, Input } from "antd";
import queryClient from "api/queryClient.ts";
import {
    useGetIntegrationMethodsExportQuery,
    useUpdateIntegrationMethodsMutation,
} from "api/TenantSlug/Integration/hooks";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { PermissionEnum } from "types/enums";
import { FormId } from "types/models.ts";

import { formSchema, ModifyIntegrationMethodsFormData } from "./formSchema";

import IntegrationData = App.Data.IntegrationData;
import StoreIntegrationConnectionSequenceStepsRequest = App.Http.Requests.Integration.StoreIntegrationConnectionSequenceStepsRequest;

interface ApiToolGeneralFormProps {
    integration: IntegrationData;
    onClose: () => void;
}

const ModifyIntegrationMethodsFormContent: FC<ApiToolGeneralFormProps> = ({ integration, onClose }) => {
    const { t } = usePaldockTranslation("forms.modify-integration-methods");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<ModifyIntegrationMethodsFormData>();
    const { control, handleSubmit, fieldNames } = generalFormUtils;
    const permissionsUtils = usePermission();

    const updateIntegrationMethodsMutation = useUpdateIntegrationMethodsMutation(integration.id ?? "");

    const onSubmitForm = (data: ModifyIntegrationMethodsFormData) => {
        const payload: StoreIntegrationConnectionSequenceStepsRequest = {
            connectionSequenceSteps: JSON.parse(data.connectionSequenceSteps),
        };

        updateIntegrationMethodsMutation.mutate(payload, {
            onSuccess: () => {
                toast.success(t("messages.update_success"));
                queryClient.refetchQueries({ queryKey: ["getIntegrationMethodsExport", integration.id] });
                queryClient.refetchQueries({ queryKey: ["getConnectionSequenceSteps"] });
                onClose();
            },
            onError: () => {
                toast.error(t("messages.update_error"));
                control._resetDefaultValues;
            },
        });
    };

    return (
        <>
            <FormItem
                control={control}
                name={fieldNames.connectionSequenceSteps?.name || "connectionSequenceSteps"}
                label={t("fields.connection_sequence_steps.label")}
            >
                <Input.TextArea rows={4} placeholder={t("fields.connection_sequence_steps.placeholder")} />
            </FormItem>
            {/* <FormItem control={control} name={fieldNames.integration?.name || "integration"} label="Integration"> // todo edit via ace editor
                <CodeEditor id={fieldNames.integration?.name}>{control._defaultValues.integration}</CodeEditor>
            </FormItem>*/}
            {permissionsUtils.hasPermission(PermissionEnum.ToolIntegrationEditorExportSave) && (
                <Button type="primary" onClick={handleSubmit(onSubmitForm)}>
                    {tC("save")}
                </Button>
            )}
        </>
    );
};

export const ModifyIntegrationMethodsForm: FC<ApiToolGeneralFormProps> = ({ integration, onClose }) => {
    const getIntegrationMethodsExportQuery = useGetIntegrationMethodsExportQuery(integration?.id || "");
    const data = getIntegrationMethodsExportQuery.data;

    if (!integration || !data) return null;

    const defaultConnectionSequenceSteps = data.connectionSequenceSteps.map((step) => ({
        ...step,
        requestConfig: { ...step.requestConfig, name: "config" },
    }));

    return (
        <GeneralFormProvider<ModifyIntegrationMethodsFormData>
            schema={formSchema}
            id={FormId.ApiToolGeneralForm}
            defaultValues={{
                connectionSequenceSteps: JSON.stringify(defaultConnectionSequenceSteps),
            }}
        >
            <ModifyIntegrationMethodsFormContent integration={integration} onClose={onClose} />
        </GeneralFormProvider>
    );
};
